import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 50px 100px;
  gap: 50px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
     font-size: 18px;
  }

`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  padding: 40px 50px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f9f9f9, #ffffff);
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.15);
  width: 300px;
  overflow-y: auto;
  word-wrap: break-word;
  text-overflow: ellipsis;
  &:hover {
    transform: translateY(-5px);
  }
`;

const DataAction = styled.div`
  color: #385963;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
    color: #91abb3;
     cursor: pointer;
  }
`;

const Title = styled.div`
  font-size: 22px;
  color: #385963;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;

    @media (max-width: 768px) {
     font-size: 18px;
  }
  
`;

const CategoriesBox = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
`;

const MainBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  h3 {
    margin-bottom: 40px;
  }

  @media (max-width: 768px) {
    text-align: center;
    h3{
      font-size: 16px;
    }
  }
`;

const Button = styled.button`
  background-color: ${(props) => (props.selected ? props.selectedColor : props.unselectedColor)};
  color: ${(props) => (props.selected ? 'white' : props.selectedColor)};
  font-weight: 400;
  border: ${(props) => props.selectedColor} solid 2px;
  padding: 10px 20px;
  margin: 5px;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.3) 0px 10px 50px;
    transition: 0.4s;
  }
  
  @media (max-width: 768px) {
   font-size: 12px;
  }
`;



const UpdateButton = styled.button`
  background-color: #4caf50;
  color: white;
  font-size: 14px;
  border: none;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #388e3c;
  }
  
  @media (max-width: 768px) {
   font-size: 12px;
  }
`;


const ClearButton = styled.button`
  background-color: #e53935;
  color: white;
  font-size: 14px;
  border: none;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #c62828;
  }
      @media (max-width: 768px) {
   font-size: 12px;
  }
`;


const Preferences = ({ profile, setProfile }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const tokenString = sessionStorage.getItem('token');
  const selectedLanguage = sessionStorage.getItem('selectedLanguage') || 'en';
  const { i18n } = useTranslation();

  const [destinationCategories, setDestinationCategories] = useState([]);
  const [experienceCategories, setExperienceCategories] = useState([]);
  const [eventCategories, setEventCategories] = useState([]);

  const [selectedDestinationCategories, setSelectedDestinationCategories] = useState([]);
  const [selectedExperienceCategories, setSelectedExperienceCategories] = useState([]);
  const [selectedEventCategories, setSelectedEventCategories] = useState([]);

  // Sync profile preferences into selected states
  useEffect(() => {
    if (profile) {
      // Safeguard: Ensure profile preferences are defined before using them
      const destinationPreferences = profile.destinationPreferences || [];
      const experiencePreferences = profile.experiencePreferences || [];
      const eventPreferences = profile.eventPreferences || [];

      // Update selected categories based on profile preferences
      const selectedDestinations = destinationCategories.filter((category) =>
        destinationPreferences.includes(category.id)
      );
      const selectedExperiences = experienceCategories.filter((category) =>
        experiencePreferences.includes(category.id)
      );
      const selectedEvents = eventCategories.filter((category) =>
        eventPreferences.includes(category.id)
      );

      setSelectedDestinationCategories(selectedDestinations);
      setSelectedExperienceCategories(selectedExperiences);
      setSelectedEventCategories(selectedEvents);
    }
  }, [profile, destinationCategories, experienceCategories, eventCategories]);

  // Fetch categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, expRes, eventRes] = await Promise.all([
          fetch(`${BASE_URL}DestinationCategory`),
          fetch(`${BASE_URL}ExperienceCategory`),
          fetch(`${BASE_URL}EventCategory`),
        ]);

        if (!destRes.ok || !expRes.ok || !eventRes.ok) {
          throw new Error('Failed to fetch categories');
        }

        const destData = await destRes.json();
        const expData = await expRes.json();
        const eventData = await eventRes.json();

        setDestinationCategories(destData);
        setExperienceCategories(expData);
        setEventCategories(eventData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchData();
  }, [BASE_URL]);

  const toggleCategory = (selectedList, setSelectedList, category) => {
    if (!category || !category.id) return; // Defensive check

    const exists = selectedList.some((c) => c && c.id === category.id);
    if (exists) {
      setSelectedList(selectedList.filter((c) => c && c.id !== category.id));
    } else {
      setSelectedList([...selectedList, category]);
    }
  };

  const updatePreferences = async (categoryKey, selectedCategories) => {
    const payload = {
      [categoryKey]: selectedCategories.map((cat) => cat.id), // Send only IDs
    };

    try {
      const response = await fetch(`${BASE_URL}RegisteredUserProfile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenString}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

          // Re-fetch profile after updating preferences
    const updatedProfile = await fetchProfile();
    // Update the profile state with the newly fetched profile
    setProfile(updatedProfile);
    } catch (error) {
      console.error(`Error updating ${categoryKey}:`, error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await fetch(
        `${BASE_URL}RegisteredUserProfile/get`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${tokenString}`,
          },
        }
      );
  
      if (!response.ok) {
        throw new Error("Failed to fetch updated profile");
      }
  
      const updatedProfile = await response.json();
      return updatedProfile;
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  return (
    <MainBox>
      <h3>{i18n.t('selectFavPreferences')}</h3>
      <Container>
        {/* Destination Card */}
        <Card>
          <Title>{i18n.t('destinations')}</Title>
          <CategoriesBox>
            {destinationCategories.map((category) => (
              <Button
                key={category.id}
                selected={selectedDestinationCategories.some((c) => c && c.id === category.id)} // Check if the category is in selectedDestinationCategories
                onClick={() =>
                  toggleCategory(selectedDestinationCategories, setSelectedDestinationCategories, category)
                }
                selectedColor="#385963"
                unselectedColor="#A7C7E7"
              >
                {category.name[selectedLanguage]}
              </Button>
            ))}
          </CategoriesBox>
          <UpdateButton onClick={() => updatePreferences('destinationPreferences', selectedDestinationCategories)}>
            {i18n.t('updatePreferences')}
          </UpdateButton>
          <ClearButton
            onClick={() => {
              setSelectedDestinationCategories([]); // Reset the selected categories
              updatePreferences('destinationPreferences', []);
            }}
          >
            {i18n.t('clear')}
          </ClearButton>
        </Card>

        {/* Experience Card */}
        <Card>
          <Title>{i18n.t('experiences')}</Title>
          <CategoriesBox>
            {experienceCategories.map((category) => (
              <Button
                key={category.id}
                selected={selectedExperienceCategories.some((c) => c && c.id === category.id)} // Check if the category is in selectedExperienceCategories
                onClick={() =>
                  toggleCategory(selectedExperienceCategories, setSelectedExperienceCategories, category)
                }
                selectedColor="#6A1B9A"
                unselectedColor="#D1C4E9"
              >
                {category.name[selectedLanguage]}
              </Button>
            ))}
          </CategoriesBox>
          <UpdateButton onClick={() => updatePreferences('experiencePreferences', selectedExperienceCategories)}>
            {i18n.t('updatePreferences')}
          </UpdateButton>
          <ClearButton
            onClick={() => {
              setSelectedExperienceCategories([]); // Reset the selected categories
              updatePreferences('experiencePreferences', []);
            }}
          >
            {i18n.t('clear')}
          </ClearButton>
        </Card>

        {/* Event Card */}
        <Card>
          <Title>{i18n.t('events')}</Title>
          <CategoriesBox>
            {eventCategories.map((category) => (
              <Button
                key={category.id}
                selected={selectedEventCategories.some((c) => c && c.id === category.id)} // Check if the category is in selectedEventCategories
                onClick={() => toggleCategory(selectedEventCategories, setSelectedEventCategories, category)}
                selectedColor="#BF360C"
                unselectedColor="#FFAB91"
              >
                {category.name[selectedLanguage]}
              </Button>
            ))}
          </CategoriesBox>
          <UpdateButton onClick={() => updatePreferences('eventPreferences', selectedEventCategories)}>
            {i18n.t('updatePreferences')}
          </UpdateButton>
          <ClearButton
            onClick={() => {
              setSelectedEventCategories([]); // Reset the selected categories
              updatePreferences('eventPreferences', []);
            }}
          >
            {i18n.t('clear')}
          </ClearButton>
        </Card>
      </Container>
    </MainBox>
  );
};

export default Preferences;
